import { useEffect, useState } from "react";

// react-slick (0.31) applies `responsive` breakpoints through matchMedia
// `change` listeners only and never checks the initial match, so on a fresh
// load at a narrow width (e.g. mobile Safari) the base settings stick until a
// resize/rotation. This hook resolves the breakpoint from window.innerWidth.
//
// `breakpoints` uses the same shape as react-slick's `responsive` option
// ({ breakpoint, settings: { slidesToShow, slidesToScroll } }) with max-width
// semantics: the smallest breakpoint >= the window width wins.
const resolve = (width, base, breakpoints) => {
  const match = [...breakpoints]
    .sort((a, b) => a.breakpoint - b.breakpoint)
    .find((b) => width <= b.breakpoint);
  return { ...base, ...(match ? match.settings : {}) };
};

const useResponsiveSlides = (base, breakpoints) => {
  const [state, setState] = useState({ ready: false, ...base });

  useEffect(() => {
    let current = null;
    const update = () => {
      const next = resolve(window.innerWidth, base, breakpoints);
      const key = `${next.slidesToShow}/${next.slidesToScroll}`;
      if (key === current) return; // resize fires often on iOS (address bar)
      current = key;
      setState({
        ready: true,
        slidesToShow: next.slidesToShow,
        slidesToScroll: next.slidesToScroll,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
    // base/breakpoints are static per component instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.slidesToShow, base.slidesToScroll]);

  return state;
};

export default useResponsiveSlides;
